import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthContextType, User } from "@/types";
import { useToast } from "@/hooks/use-toast";
import { getBrowserInfo, getDeviceInfo, getDeviceId } from "@/utils/deviceDetection";
import { getRouteSpecificCacheKey, shouldProcessEvent, isInRoute } from "@/utils/routeManager";
import SessionConflictDialog from "@/components/SessionConflictDialog";

interface UserSession {
  id: string;
  user_id: string;
  device_id: string;
  browser: string;
  device: string;
  is_active: boolean;
  last_active: string;
  created_at: string;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  login: async () => {},
  loginWithEmail: async () => {},
  signUpWithEmail: async () => {},
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [sessionConflict, setSessionConflict] = useState(false);
  const [existingSession, setExistingSession] = useState<any>(null);
  const [pendingSessionData, setPendingSessionData] = useState<any>(null);
  
  // Route-aware controls para isolamento por contexto
  const lastProcessedEvent = useRef<string>('');
  const lastEventTime = useRef<number>(0);
  const authStateInitialized = useRef(false);
  const sessionProcessing = useRef(false);
  const stableStateReached = useRef(false);
  const userDataCache = useRef<Map<string, User>>(new Map());
  const cacheTimestamp = useRef<Map<string, number>>(new Map());
  const routeSpecificProcessing = useRef<Set<string>>(new Set());
  
  // Heartbeat control - CORRIGIDO
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  const isHeartbeatActive = useRef(false);

  // Activity detection refs
  const lastActivityTime = useRef<number>(Date.now());
  const activityListenersSetup = useRef(false);
  const inactivityTimeout = useRef<NodeJS.Timeout | null>(null);
  const isUserActive = useRef(true);
  const INACTIVITY_THRESHOLD = 2 * 60 * 1000; // 2 minutos

  // Circuit breaker para evitar loops infinitos
  const eventCounter = useRef<Map<string, number>>(new Map());
  const circuitBreakerThreshold = 10;
  const circuitBreakerResetTime = 60000; // 1 minuto

  const isCircuitBreakerTripped = (eventKey: string): boolean => {
    const count = eventCounter.current.get(eventKey) || 0;
    return count >= circuitBreakerThreshold;
  };

  const incrementEventCounter = (eventKey: string) => {
    const count = eventCounter.current.get(eventKey) || 0;
    eventCounter.current.set(eventKey, count + 1);
    
    // Reset after threshold time
    setTimeout(() => {
      eventCounter.current.delete(eventKey);
    }, circuitBreakerResetTime);
  };

  // Função para detectar atividade do usuário
  const detectUserActivity = () => {
    const now = Date.now();
    lastActivityTime.current = now;
    
    // Se estava inativo, marcar como ativo novamente
    if (!isUserActive.current && user) {
      console.log('Auth: Usuário voltou a ficar ativo');
      isUserActive.current = true;
      updateUserActivity(user.id, true);
    }
    
    // Reset do timeout de inatividade
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
    
    // Configurar novo timeout
    inactivityTimeout.current = setTimeout(() => {
      if (user && isUserActive.current) {
        console.log('Auth: Usuário inativo por mais de 2 minutos - marcando como offline');
        isUserActive.current = false;
        updateUserActivity(user.id, false);
      }
    }, INACTIVITY_THRESHOLD);
  };

  // Configurar listeners de atividade
  const setupActivityListeners = () => {
    if (activityListenersSetup.current) return;
    
    console.log('Auth: Configurando listeners de atividade');
    activityListenersSetup.current = true;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, detectUserActivity, { passive: true });
    });
    
    // Detectar quando usuário fecha aba/browser - MELHORADO
    window.addEventListener('beforeunload', () => {
      if (user) {
        // Usar sendBeacon para garantir que a requisição seja enviada
        const data = JSON.stringify({
          user_id: user.id,
          is_active: false,
          last_active: new Date().toISOString()
        });
        
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/api/user-offline', data);
        } else {
          // Fallback síncrono
          updateUserActivity(user.id, false);
        }
      }
    });
    
    // Detectar quando aba perde/ganha foco
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && user) {
        console.log('Auth: Aba ficou oculta - pausando atividade');
      } else if (!document.hidden && user) {
        console.log('Auth: Aba voltou ao foco - retomando atividade');
        detectUserActivity();
      }
    });
  };

  // Atualizar atividade do usuário no banco
  const updateUserActivity = async (userId: string, isActive: boolean) => {
    try {
      const deviceId = getDeviceId();
      const now = new Date().toISOString();
      
      console.log(`Auth: Atualizando atividade do usuário: ${isActive ? 'ATIVO' : 'INATIVO'}`);
      
      // Atualizar user_sessions
      const { error: sessionError } = await supabase
        .from('user_sessions')
        .update({ 
          is_active: isActive,
          last_active: now
        })
        .eq('user_id', userId)
        .eq('device_id', deviceId);
      
      if (sessionError) {
        console.error("Auth: Erro ao atualizar sessão:", sessionError);
      }
      
      // Atualizar user_stats
      const { error: statsError } = await supabase
        .from('user_stats')
        .upsert({
          user_id: userId,
          is_online: isActive,
          last_active: now,
          updated_at: now
        });
      
      if (statsError) {
        console.error("Auth: Erro ao atualizar stats:", statsError);
      }
      
    } catch (error) {
      console.error("Auth: Erro ao atualizar atividade:", error);
    }
  };

  // NOVO: Função de heartbeat mais robusta baseada em atividade
  const startHeartbeat = (userId: string) => {
    if (isHeartbeatActive.current || !userId) {
      console.log('Auth: Heartbeat já ativo ou sem usuário');
      return;
    }

    console.log('Auth: Iniciando heartbeat para usuário:', userId);
    isHeartbeatActive.current = true;
    
    // Configurar listeners de atividade
    setupActivityListeners();
    
    // Marcar como ativo inicialmente
    isUserActive.current = true;
    detectUserActivity();

    const updateSession = async () => {
      try {
        // Só atualizar se o usuário estiver ativo
        if (isUserActive.current) {
          const deviceId = getDeviceId();
          const now = new Date().toISOString();
          
          console.log('Auth: Atualizando sessão via heartbeat (usuário ativo)');
          
          const { error } = await supabase
            .from('user_sessions')
            .update({ 
              last_active: now,
              is_active: true 
            })
            .eq('user_id', userId)
            .eq('device_id', deviceId);
          
          if (error) {
            console.error("Auth: Erro no heartbeat:", error);
          }
        } else {
          console.log('Auth: Usuário inativo - não atualizando heartbeat');
        }
      } catch (error) {
        console.error("Auth: Erro no heartbeat:", error);
      }
    };

    // Primeira atualização imediata
    updateSession();
    
    // Optimized heartbeat: every 2 minutes instead of 30 seconds
    heartbeatInterval.current = setInterval(updateSession, 120000);
  };

  const stopHeartbeat = () => {
    console.log('Auth: Parando heartbeat');
    isHeartbeatActive.current = false;
    isUserActive.current = false;
    
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
    
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
      inactivityTimeout.current = null;
    }
    
    // Remover listeners de atividade
    if (activityListenersSetup.current) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.removeEventListener(event, detectUserActivity);
      });
      activityListenersSetup.current = false;
    }
  };

  // Debounce mais restritivo com verificação de rota
  const debounceAuth = (event: string, session: any, callback: () => void) => {
    const now = Date.now();
    const routeContext = window.location.pathname;
    const eventKey = `${event}_${session?.user?.id || 'null'}_${routeContext}`;
    
    // Circuit breaker check
    if (isCircuitBreakerTripped(eventKey)) {
      console.log(`Auth: Circuit breaker ativo para evento ${event} na rota ${routeContext}`);
      return;
    }

    // Debounce com rota específica
    if (eventKey === lastProcessedEvent.current && (now - lastEventTime.current) < 5000) {
      console.log(`Auth: Evento duplicado ignorado (route-aware): ${event} em ${routeContext}`);
      return;
    }
    
    // Se já atingimos estado estável na rota atual, ignorar eventos SIGNED_IN duplicados
    const routeStableKey = `stable_${routeContext}`;
    if (routeSpecificProcessing.current.has(routeStableKey) && event === 'SIGNED_IN' && user) {
      console.log(`Auth: Estado estável na rota ${routeContext} - ignorando SIGNED_IN duplicado`);
      return;
    }
    
    lastProcessedEvent.current = eventKey;
    lastEventTime.current = now;
    incrementEventCounter(eventKey);
    callback();
  };

  // Cache isolado por rota com throttling
  const getCachedUserData = async (userId: string): Promise<User | null> => {
    const now = Date.now();
    const CACHE_DURATION = 30000; // 30 segundos
    const cacheKey = getRouteSpecificCacheKey(`user_${userId}`);
    
    const cachedUser = userDataCache.current.get(cacheKey);
    const cacheTime = cacheTimestamp.current.get(cacheKey) || 0;
    
    if (cachedUser && (now - cacheTime) < CACHE_DURATION) {
      console.log(`Auth: Usando dados do cache para rota: ${window.location.pathname}`);
      return cachedUser;
    }
    
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (error) {
        console.error("Error fetching user data:", error);
        return cachedUser || null;
      }
      
      if (userData) {
        userDataCache.current.set(cacheKey, userData);
        cacheTimestamp.current.set(cacheKey, now);
        console.log(`Auth: User data atualizado no cache para rota: ${window.location.pathname}`);
        return userData;
      }
    } catch (error) {
      console.error("Error in getCachedUserData:", error);
    }
    
    return cachedUser || null;
  };

  useEffect(() => {
    const handleHashParams = async () => {
      try {
        if (window.location.hash && window.location.hash.includes('access_token')) {
          console.log("Hash parameters detected, attempting to set session");
          
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Error getting session from hash:", error);
            toast({
              title: "Erro na autenticação",
              description: "Não foi possível completar o login. Por favor, tente novamente.",
              variant: "destructive",
            });
          } else if (data.session) {
            console.log("Successfully retrieved session from hash");
            if (window.history && window.history.replaceState) {
              const cleanUrl = window.location.pathname + window.location.search;
              window.history.replaceState(null, "", cleanUrl);
            }
          }
        }
      } catch (error) {
        console.error("Error handling hash params:", error);
      }
    };

    handleHashParams();
  }, []);

  const updateUserSession = async (userId: string) => {
    const routeContext = window.location.pathname;
    const sessionKey = `session_${userId}_${routeContext}`;
    
    if (sessionProcessing.current || routeSpecificProcessing.current.has(sessionKey)) {
      console.log(`Auth: Sessão já sendo processada para rota ${routeContext}, ignorando`);
      return true;
    }
    
    routeSpecificProcessing.current.add(sessionKey);
    sessionProcessing.current = true;
    
    try {
      const deviceId = getDeviceId();
      const browser = getBrowserInfo();
      const device = getDeviceInfo();
      
      console.log(`Auth: Updating user session para rota ${routeContext}:`, {userId, deviceId, browser, device});
      
      const { data: existingSessions, error: fetchError } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_id', userId)
        .neq('device_id', deviceId)
        .eq('is_active', true)
        .order('last_active', { ascending: false })
        .limit(1);
      
      if (fetchError) {
        console.error("Error checking existing sessions:", fetchError);
        return true;
      }
      
      if (existingSessions && existingSessions.length > 0) {
        console.log("Existing active session found:", existingSessions[0]);
        setExistingSession(existingSessions[0]);
        setPendingSessionData({userId, deviceId, browser, device});
        setSessionConflict(true);
        return false;
      }
      
      // AJUSTE: UPSERT para evitar conflito 409 com unique_user_device constraint
      const { error: sessionError } = await supabase
        .from('user_sessions')
        .upsert({
          user_id: userId,
          device_id: deviceId,
          browser,
          device,
          is_active: true,
          last_active: new Date().toISOString()
        }, {
          onConflict: 'user_id,device_id'
        });
      
      if (sessionError) {
        console.error("Error creating session:", sessionError);
      } else {
        console.log("Auth: Sessão criada/atualizada com sucesso");
      }
      
      return true;
    } catch (error) {
      console.error("Error managing user session:", error);
      return true;
    } finally {
      sessionProcessing.current = false;
      routeSpecificProcessing.current.delete(sessionKey);
    }
  };

  const terminateOtherSessions = async (userId: string) => {
    try {
      const deviceId = getDeviceId();
      
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_active: false })
        .eq('user_id', userId)
        .neq('device_id', deviceId);
      
      if (error) {
        console.error("Error terminating other sessions:", error);
      }
      
      return true;
    } catch (error) {
      console.error("Error terminating sessions:", error);
      return false;
    }
  };

  const handleSessionConflictContinue = async () => {
    if (!pendingSessionData) return;
    
    setIsLoading(true);
    const terminated = await terminateOtherSessions(pendingSessionData.userId);
    
    if (terminated) {
      await updateUserSession(pendingSessionData.userId);
      
      toast({
        title: "Sessão transferida",
        description: "A sessão anterior foi encerrada e você está logado neste dispositivo agora.",
        duration: 5000,
      });
    }
    
    setSessionConflict(false);
    setPendingSessionData(null);
    setExistingSession(null);
    setIsLoading(false);
  };

  const handleSessionConflictCancel = () => {
    setSessionConflict(false);
    setPendingSessionData(null);
    setExistingSession(null);
    
    logout();
    
    toast({
      title: "Login cancelado",
      description: "A sessão ativa em outro dispositivo foi mantida.",
      duration: 5000,
    });
  };

  useEffect(() => {
    // CORREÇÃO: Executar apenas uma vez POR APLICAÇÃO, não por rota
    if (authStateInitialized.current) return;
    
    console.log("AuthProvider: Configurando listener GLOBAL de autenticação (uma única vez)");
    authStateInitialized.current = true;
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // CORREÇÃO: Processar TODOS os eventos de auth globalmente
        
        debounceAuth(event, session, async () => {
          console.log(`Auth: Processing event: ${event}, Session:`, session ? "Present" : "Null", "User ID:", session?.user?.id);
          
          if (event === "SIGNED_IN" && session) {
            setUser({
              id: session.user.id,
              email: session.user.email || '',
              display_name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || 'Usuário',
              avatar_url: session.user.user_metadata.avatar_url,
              created_at: session.user.created_at || new Date().toISOString()
            });
            
            // CORREÇÃO: Processo simplificado sem verificação de rota específica
            setTimeout(async () => {
              try {
                const sessionValid = await updateUserSession(session.user.id);
                
                if (sessionValid !== false) {
                  const userData = await getCachedUserData(session.user.id);
                  
                  if (userData) {
                    setUser(userData);
                    stableStateReached.current = true;
                    
                    // INICIAR HEARTBEAT APÓS LOGIN BEM-SUCEDIDO
                    console.log(`Auth: Estado estável atingido - iniciando heartbeat para ${session.user.id}`);
                    startHeartbeat(session.user.id);
                  }
                }

                // CORREÇÃO: Não fazer redirecionamento automático - deixar React Router cuidar
                console.log("Auth: Login bem-sucedido - estado estável atingido");
              } catch (error) {
                console.error("Error processing login:", error);
              } finally {
                setIsLoading(false);
              }
            }, 100);
          } else if (event === "SIGNED_OUT") {
            console.log("User signed out");
            
            // PARAR HEARTBEAT NO LOGOUT
            stopHeartbeat();
            
            if (user) {
              try {
                const deviceId = getDeviceId();
                await supabase
                  .from('user_sessions')
                  .update({ is_active: false })
                  .eq('user_id', user.id)
                  .eq('device_id', deviceId);
              } catch (error) {
                console.error("Error deactivating session on logout:", error);
              }
            }
            
            // Limpar cache específico da rota
            const routeCacheKeys = Array.from(userDataCache.current.keys()).filter(key => 
              key.includes(window.location.pathname.split('/')[1] || 'general')
            );
            routeCacheKeys.forEach(key => {
              userDataCache.current.delete(key);
              cacheTimestamp.current.delete(key);
            });
            
            setUser(null);
            stableStateReached.current = false;
            routeSpecificProcessing.current.clear();
            setIsLoading(false);
          } else if (event === "TOKEN_REFRESHED") {
            console.log("Auth: Token refreshed silently - sem ações adicionais");
          }
        });
      }
    );

    const checkSession = async () => {
      try {
        console.log("Auth: Initial session check (ÚNICO CHECK)");
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log("Auth: Session found:", session.user.id);
          
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            display_name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || 'Usuário',
            avatar_url: session.user.user_metadata.avatar_url,
            created_at: session.user.created_at || new Date().toISOString()
          });
          
          setTimeout(async () => {
            try {
              const sessionValid = await updateUserSession(session.user.id);
              
              if (sessionValid !== false) {
                const userData = await getCachedUserData(session.user.id);
                
                if (userData) {
                  setUser(userData);
                  stableStateReached.current = true;
                  console.log('Auth: Estado estável atingido no check inicial - iniciando heartbeat');
                  
                  // INICIAR HEARTBEAT NO CHECK INICIAL
                  startHeartbeat(session.user.id);
                }
              }
            } catch (error) {
              console.error("Error fetching user data:", error);
            } finally {
              setIsLoading(false);
            }
          }, 100);
        } else {
          console.log("Auth: No session found");
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error checking auth session:", error);
        setIsLoading(false);
      }
    };

    checkSession();

    return () => {
      console.log("Auth: Cleanup - unsubscribing from auth state changes");
      subscription.unsubscribe();
      stopHeartbeat(); // PARAR HEARTBEAT NO CLEANUP
      authStateInitialized.current = false;
      stableStateReached.current = false;
      routeSpecificProcessing.current.clear();
    };
  }, []);

  const login = async () => {
    try {
      setIsLoading(true);
      console.log("Starting Google login...");
      
      const currentUrl = window.location.origin;
      
      const redirectUrl = currentUrl;
      
      console.log(`Login redirect configured to: ${redirectUrl}`);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectUrl,
        },
      });

      if (error) {
        console.error("Error with Google login:", error);
        throw error;
      }
      
      console.log("Google login initiated, redirecting...", data);
    } catch (error) {
      console.error("Error during login:", error);
      toast({
        title: "Falha no login",
        description: "Houve um problema ao fazer login com o Google. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error: any) {
      console.error("Error during email login:", error);
      toast({
        title: "Falha no login",
        description: error.message === "Invalid login credentials"
          ? "Email ou senha incorretos."
          : "Houve um problema ao fazer login. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;

      if (data.user && !data.session) {
        toast({
          title: "Verifique seu email",
          description: "Enviamos um link de confirmação para o seu email.",
        });
      }
    } catch (error: any) {
      console.error("Error during sign up:", error);
      toast({
        title: "Falha no cadastro",
        description: error.message || "Houve um problema ao criar sua conta. Tente novamente.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      console.log("Starting logout...");
      
      // PARAR HEARTBEAT ANTES DO LOGOUT
      stopHeartbeat();
      
      if (user) {
        const deviceId = getDeviceId();
        const { error: sessionError } = await supabase
          .from('user_sessions')
          .update({ is_active: false })
          .eq('user_id', user.id)
          .eq('device_id', deviceId);
          
        if (sessionError) {
          console.error("Error deactivating session:", sessionError);
        }
      }
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Error during logout:", error);
        throw error;
      }
      
      setUser(null);
      stableStateReached.current = false;
      userDataCache.current = new Map();
      cacheTimestamp.current = new Map();
      console.log("Logout successful");
      
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
    } catch (error) {
      console.error("Error during logout:", error);
      toast({
        title: "Falha no logout",
        description: "Houve um problema ao desconectar. Por favor, tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, loginWithEmail, signUpWithEmail, logout }}>
      <SessionConflictDialog
        isOpen={sessionConflict}
        existingSession={existingSession}
        onContinue={handleSessionConflictContinue}
        onCancel={handleSessionConflictCancel}
      />
      {children}
    </AuthContext.Provider>
  );
};
